import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  FileText, 
  Settings, 
  Heart, 
  ArrowRight, 
  Loader2, 
  Box, 
  ClipboardCheck,
  ChevronRight,
  TrendingUp,
  Clock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { orderService, jobCardService } from '../../../../services/apiService';
import { useAuth } from '../../../../auth/AuthContext';
import { getDisplayNameForUI } from '../../../../utils/displayName';

const Overview = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ordersCount, setOrdersCount] = useState(0);
  const [jobsCount, setJobsCount] = useState(0);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [ordersRes, jobsData] = await Promise.all([
          orderService.getOrders().catch(() => ({ orders: [] })),
          jobCardService.getMy().catch(() => []),
        ]);
        const orders = ordersRes?.orders ?? ordersRes?.data?.orders ?? [];
        const jobs = Array.isArray(jobsData) ? jobsData : jobsData?.jobCards ?? jobsData?.jobs ?? [];
        setOrdersCount(orders.length);
        setJobsCount(jobs.length);
        setRecentOrders(orders.slice(0, 5));
        setRecentJobs(jobs.slice(0, 5));
      } catch (e) {
        setRecentOrders([]);
        setRecentJobs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const cards = [
    {
      label: 'Orders',
      value: ordersCount,
      icon: ShoppingBag,
      path: '/customer/dashboard/orders',
      color: 'bg-indigo-500',
      description: 'Items you purchased'
    },
    {
      label: 'Service Jobs',
      value: jobsCount,
      icon: Settings,
      path: '/customer/dashboard/jobs',
      color: 'bg-emerald-500',
      description: 'Maintenance & Repairs'
    },
    {
      label: 'Invoices',
      value: '—',
      icon: FileText,
      path: '/customer/dashboard/invoices',
      color: 'bg-amber-500',
      description: 'Payment records'
    },
    {
      label: 'Wishlist',
      value: '—',
      icon: Heart,
      path: '/customer/dashboard/wishlist',
      color: 'bg-rose-500',
      description: 'Your saved items'
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 className="animate-spin text-4xl text-primary-500 mb-4" />
        <p className="text-slate-500 font-medium animate-pulse">Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-10"
    >
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <motion.h2 
            variants={itemVariants}
            className="text-3xl font-bold text-slate-900 tracking-tight"
          >
            Welcome back, <span className="text-primary-600">{getDisplayNameForUI(user) || 'Customer'}</span>
          </motion.h2>
          <motion.p 
            variants={itemVariants}
            className="text-slate-500 mt-1 flex items-center gap-2"
          >
            Managing your automotive world at a glance. <TrendingUp size={16} className="text-emerald-500" />
          </motion.p>
        </div>
        <motion.div variants={itemVariants}>
          <button 
            onClick={() => navigate('/service/booking')}
            className="px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold shadow-lg hover:bg-slate-800 transition-all flex items-center gap-2 active:scale-95"
          >
            New Service Booking <ArrowRight size={18} />
          </button>
        </motion.div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <motion.button
              key={card.path}
              variants={itemVariants}
              whileHover={{ scale: 1.02, translateY: -5 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(card.path)}
              className="group relative overflow-hidden stat-card-glass p-6 text-left"
            >
              <div className={`absolute top-0 right-0 w-24 h-24 ${card.color} opacity-5 blur-2xl rounded-full -mr-12 -mt-12 group-hover:opacity-10 transition-opacity`} />
              
              <div className="flex items-start justify-between mb-4">
                <div className={`${card.color} p-3 rounded-xl shadow-lg inner-glow text-white`}>
                  <Icon size={24} />
                </div>
                <ChevronRight className="text-slate-300 group-hover:text-slate-400 group-hover:translate-x-1 transition-all" />
              </div>
              
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-1">{card.label}</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black text-slate-900">{card.value}</span>
                  {card.value !== '—' && <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">+12%</span>}
                </div>
                <p className="text-xs text-slate-500 mt-2">{card.description}</p>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div variants={itemVariants} className="dashboard-card group">
          <div className="dashboard-card-header flex items-center justify-between border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600">
                <Box size={18} />
              </div>
              <h3 className="font-bold text-slate-800">Recent Orders</h3>
            </div>
            <button
              onClick={() => navigate('/customer/dashboard/orders')}
              className="text-xs font-bold text-primary-600 hover:text-primary-700 bg-primary-50 px-3 py-1.5 rounded-lg transition-colors"
            >
              VIEW ALL
            </button>
          </div>
          <div className="p-2">
            {recentOrders.length === 0 ? (
              <div className="py-12 text-center flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
                  <ShoppingBag size={32} />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">No orders yet</p>
                  <p className="text-xs text-slate-500 max-w-[200px] mx-auto mt-1">Start exploring our premium parts collection.</p>
                </div>
                <button
                  onClick={() => navigate('/')}
                  className="mt-2 text-sm font-bold text-primary-600 hover:underline"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                {recentOrders.map((order) => (
                  <button
                    key={order.orderId || order._id}
                    onClick={() => navigate('/customer/dashboard/orders')}
                    className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 rounded-xl transition-all text-left group/item"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover/item:bg-white group-hover/item:shadow-sm transition-all">
                        <Box size={18} />
                      </div>
                      <div>
                        <span className="font-bold text-slate-900 block truncate w-32 sm:w-auto text-sm">
                          Order #{String(order.orderId || order._id || '').slice(-8).toUpperCase()}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1 mt-0.5">
                          <Clock size={10} /> 2 days ago
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        order.status === 'delivered' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                      }`}>
                        {order.status || 'Pending'}
                      </span>
                      <ChevronRight size={16} className="text-slate-300 group-hover/item:translate-x-1 transition-all" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="dashboard-card group">
          <div className="dashboard-card-header flex items-center justify-between border-slate-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                <Settings size={18} />
              </div>
              <h3 className="font-bold text-slate-800">Active Services</h3>
            </div>
            <button
              onClick={() => navigate('/customer/dashboard/jobs')}
              className="text-xs font-bold text-primary-600 hover:text-primary-700 bg-primary-50 px-3 py-1.5 rounded-lg transition-colors"
            >
              SERVICE HISTORY
            </button>
          </div>
          <div className="p-2">
            {recentJobs.length === 0 ? (
              <div className="py-12 text-center flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-300">
                  <ClipboardCheck size={32} />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">No active jobs</p>
                  <p className="text-xs text-slate-500 max-w-[200px] mx-auto mt-1">Your vehicle health is our top priority.</p>
                </div>
                <button
                  onClick={() => navigate('/service/booking')}
                  className="mt-2 text-sm font-bold text-emerald-600 hover:underline"
                >
                  Book a Service
                </button>
              </div>
            ) : (
              <div className="space-y-1">
                {recentJobs.map((job) => (
                  <button
                    key={job._id || job.id}
                    onClick={() => navigate(`/service/job/${job._id || job.id}`)}
                    className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-50 rounded-xl transition-all text-left group/item"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover/item:bg-white group-hover/item:shadow-sm transition-all">
                        <Settings size={18} />
                      </div>
                      <div>
                        <span className="font-bold text-slate-900 block truncate w-32 sm:w-auto text-sm">
                          Job #{String(job.jobCardId || job._id || job.id || '').slice(-8).toUpperCase()}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1 mt-0.5 uppercase tracking-tight">
                          {job.vehicleInfo?.make || 'Vehicle'} {job.vehicleInfo?.model || ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        ['InProgress', 'ReadyForQC'].includes(job.status) ? 'bg-indigo-50 text-indigo-600 animate-pulse' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {job.status?.replace('_', ' ') || 'Intake'}
                      </span>
                      <ChevronRight size={16} className="text-slate-300 group-hover/item:translate-x-1 transition-all" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Overview;
